import { useEffect, useState } from 'react';
import type { WorkspaceProposalRecord } from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { useApp } from '../contexts/AppContext';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';

function statusVariant(status: string): 'success' | 'secondary' | 'default' {
  if (status === 'applied') return 'success';
  if (status === 'discarded') return 'secondary';
  return 'default';
}

function roleVariant(role: string): 'default' | 'secondary' | 'outline' {
  if (role === 'orchestrator') return 'default';
  if (role === 'reviewer') return 'secondary';
  return 'outline';
}

export function BuilderPage() {
  const { workspaces, workspaceId } = useApp();
  const workspace = workspaces.find((w) => w.id === workspaceId);
  const [proposals, setProposals] = useState<WorkspaceProposalRecord[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [prompt, setPrompt] = useState('');

  const selected = proposals.find((p) => p.id === selectedId) ?? null;

  useEffect(() => {
    if (!workspaceId) return;
    void apiClient.listProposals().then(setProposals).catch(() => setProposals([]));
  }, [workspaceId]);

  function select(p: WorkspaceProposalRecord) {
    setSelectedId(p.id);
    setApplied('');
    setChangeSummary('');
  }

  async function propose() {
    const text = prompt.trim();
    if (!text || busy) return;
    setBusy(true);
    setApplied('');
    setChangeSummary('');
    try {
      const created = await apiClient.propose(text);
      setProposals((list) => [created, ...list]);
      setSelectedId(created.id);
      setPrompt('');
    } catch {
      /* leave the prompt so the operator can retry */
    } finally {
      setBusy(false);
    }
  }

  async function apply(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      const result = await apiClient.applyProposal(id);
      setApplied(
        `Applied: ${result.agents} agents, ${result.relations} relations, ${result.taxonomy} routes, ${result.reviewPolicies} policies, ${result.workflowTypes} workflow types, ${result.tools} tools, ${result.skills} skills, ${result.integrations} integrations, ${result.triggers} triggers.`,
      );
      if (result.changeSummary) setChangeSummary(result.changeSummary);
      const next = await apiClient.listProposals();
      setProposals(next);
    } catch {
      /* keep the proposal selected */
    } finally {
      setBusy(false);
    }
  }

  async function discard(id: string) {
    await apiClient.discardProposal(id);
    if (selectedId === id) setSelectedId(null);
    const next = await apiClient.listProposals();
    setProposals(next);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div>
        <h1 className="text-2xl font-semibold">AI Trainer</h1>
        <p className="text-sm text-gray-500">
          Describe your team, process, or desired changes. The trainer sees your current workspace and proposes a complete operating model for{' '}
          <strong>{workspace?.name || 'no workspace'}</strong>.
        </p>
      </div>

      {!workspaceId ? (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm dark:border-gray-700 dark:bg-gray-800">
          Select a workspace from the sidebar to use the trainer.
        </div>
      ) : (
        <div className="mt-4 grid min-h-0 flex-1 gap-4 lg:grid-cols-2">
          <section className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h2 className="text-sm font-semibold">Describe the operating model</h2>
            <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100">
              The trainer always sees your current workspace. Describe a new process to create a greenfield model, or describe changes to add, modify, or remove from the existing setup.
            </div>
            <textarea
              className="mt-3 min-h-80 w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              placeholder="Describe your team or process, or changes you want to make. e.g. 'We run a SaaS support desk handling billing/refund questions and technical bugs.' or 'Add a Compliance Reviewer that reviews all billing outputs.'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />
            <Button className="mt-3 w-fit" size="sm" disabled={!prompt.trim() || busy} onClick={() => void propose()}>
              {busy ? 'Working...' : 'Propose model'}
            </Button>
            {proposals.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-gray-400">History</div>
                <div className="mt-1 flex flex-col gap-1">
                  {proposals.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`flex items-center justify-between rounded px-2 py-1 text-left text-xs hover:bg-gray-100 dark:hover:bg-gray-800 ${p.id === selectedId ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                      onClick={() => select(p)}
                    >
                      <span className="truncate">{p.summary || p.prompt}</span>
                      <Badge variant={statusVariant(p.status)}>{p.status}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            {selected ? (
              <>
                <div className="flex items-start justify-between">
                  <h2 className="text-sm font-semibold">Proposed model</h2>
                  <Badge variant={statusVariant(selected.status)}>{selected.status}</Badge>
                </div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{selected.summary}</p>

                <div className="mt-3">
                  <div className="text-xs uppercase tracking-wide text-gray-400">Agents</div>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {selected.proposal.agents.map((a) => (
                      <div key={a.name} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                        <div className="flex items-center gap-2">
                          <strong className="text-sm">{a.name}</strong>
                          <Badge variant={roleVariant(a.agentRole)}>{a.agentRole}</Badge>
                          <span className="text-[10px] text-gray-400">T{a.tier}</span>
                        </div>
                        <div className="text-xs text-gray-500">{a.jobDescription}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {(selected.proposal.relations || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Hierarchy</div>
                    <div className="mt-1 flex flex-col gap-1">
                      {selected.proposal.relations.map((r, i) => (
                        <div key={`${r.parentName}-${r.childName}-${i}`} className="text-xs">
                          {r.parentName} → {r.childName}{' '}
                          <span className="text-gray-400">({r.relationType})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.proposal.taxonomy.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Intake routes</div>
                    <div className="mt-1 flex flex-col gap-1">
                      {selected.proposal.taxonomy.map((t) => (
                        <div key={t.key} className="text-xs">
                          <code>{t.key}</code> → {t.targetAgentName || 'orchestrator'}{' '}
                          <span className="text-gray-400">({t.keywords.join(', ')})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.proposal.reviewPolicies.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Review policies</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selected.proposal.reviewPolicies.map((rp, i) => (
                        <Badge key={i} variant="secondary" className="font-normal">
                          {rp.labelKey || 'default'}: {rp.executionMode}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.proposal.workflowTypes || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Workflow types</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selected.proposal.workflowTypes.map((wf) => (
                        <Badge key={wf.key} variant="outline">
                          {wf.key}: {wf.executionMode} / {wf.reviewMode}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.proposal.tools || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Tools</div>
                    <div className="mt-1 flex flex-col gap-1">
                      {selected.proposal.tools.map((tool) => (
                        <div key={tool.key} className="text-xs">
                          <code>{tool.key}</code>{' '}
                          <span className="text-gray-400">
                            {tool.kind} · {tool.assignedAgentNames.length} assignment(s)
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.proposal.skills || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Skills</div>
                    <div className="mt-1 flex flex-col gap-1">
                      {selected.proposal.skills.map((skill) => (
                        <div key={skill.key} className="text-xs">
                          <code>{skill.key}</code>{' '}
                          <span className="text-gray-400">
                            → {skill.assignedAgentNames.join(', ') || 'unassigned'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.proposal.integrations || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Draft integrations</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {selected.proposal.integrations.map((integration) => (
                        <Badge key={integration.key} variant="warning">
                          {integration.key} · credentials required
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {(selected.proposal.triggers || []).length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-wide text-gray-400">Triggers</div>
                    <div className="mt-1 flex flex-col gap-1">
                      {selected.proposal.triggers.map((trigger, i) => (
                        <div key={`${trigger.name}-${i}`} className="text-xs">
                          {trigger.name}{' '}
                          <span className="text-gray-400">
                            {trigger.type} → {trigger.targetAgentName || 'workspace default'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={selected.status === 'applied' || busy}
                    onClick={() => void apply(selected.id)}
                  >
                    {selected.status === 'applied' ? 'Applied' : 'Apply to workspace'}
                  </Button>
                  {selected.status !== 'applied' && (
                    <Button size="sm" variant="ghost" onClick={() => void discard(selected.id)}>
                      Discard
                    </Button>
                  )}
                </div>
                {applied && <div className="mt-2 text-xs text-green-600">{applied}</div>}
                {changeSummary && <div className="mt-1 text-xs text-blue-600">{changeSummary}</div>}
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
                Propose a model to preview it here.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
